import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { BasicInfoFields } from "./form/BasicInfoFields";
import { StatusFields } from "./form/StatusFields";
import { DateFields } from "./form/DateFields";
import { AssociationFields } from "./form/AssociationFields";
import { taskFormSchema, TaskFormValues } from "./types";
import { useState } from "react";
import { useOrganization } from "@/contexts/OrganizationContext";

interface GeneralTaskFormProps {
  leadId?: string;
  taskId?: string;
  initialData?: any;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const GeneralTaskForm = ({ leadId, taskId, initialData, onSuccess, onCancel }: GeneralTaskFormProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentOrganization } = useOrganization();

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: initialData?.title || "",
      description: initialData?.description || "",
      priority: initialData?.priority || "medium",
      status: initialData?.status || "todo",
      progress: initialData?.progress || 0,
      due_date: initialData?.due_date ? new Date(initialData.due_date) : undefined,
      reminder_date: initialData?.reminder_date ? new Date(initialData.reminder_date) : undefined,
      lead_id: initialData?.lead_id || leadId || "",
      contact_id: initialData?.contact_id || "",
      assigned_to: initialData?.assigned_to || "",
    },
  });

  const onSubmit = async (values: TaskFormValues) => {
    if (!currentOrganization?.id) {
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Get the current user session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session) throw new Error("No active session");

      // Process the field values, converting "none" to null
      const processFieldValue = (value: string | undefined | null) => {
        if (value === "none" || value === "") {
          return null;
        }
        return value;
      };

      // Format dates as ISO strings for Supabase
      const taskData = {
        title: values.title,
        description: values.description,
        priority: values.priority,
        status: values.status,
        progress: values.progress,
        due_date: values.due_date?.toISOString().split('T')[0] || null,
        reminder_date: values.reminder_date?.toISOString() || null,
        lead_id: processFieldValue(values.lead_id),
        contact_id: processFieldValue(values.contact_id),
        assigned_to: processFieldValue(values.assigned_to),
        // Note: General tasks don't have project_id
      };

      const { error } = await supabase.functions.invoke("tasks-operations", {
        body: {
          action: taskId ? "update" : "create",
          task: taskData,
          taskId: taskId,
          userId: session.user.id,
          organizationId: currentOrganization.id
        },
      });

      if (error) throw error;

      // Invalidate queries to refresh the data
      await queryClient.invalidateQueries({ queryKey: ["tasks"] });
      if (leadId) {
        await queryClient.invalidateQueries({ queryKey: ["tasks", leadId] });
      }

      toast({
        title: "Success",
        description: `Task ${taskId ? "updated" : "created"} successfully`,
      });

      form.reset();
      onSuccess?.();
    } catch (error: any) {
      console.error("Task submission error:", error);
      toast({
        title: "Error",
        description: error.message || `Failed to ${taskId ? "update" : "create"} task`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentOrganization) {
    return <div>Please select an organization to manage tasks.</div>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <BasicInfoFields form={form} />
        <StatusFields form={form} />
        <DateFields form={form} />
        <AssociationFields form={form} />
        
        <div className="flex justify-between gap-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : taskId ? "Save Changes" : "Create Task"}
          </Button>
        </div>
      </form>
    </Form>
  );
};